import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import ReactModal from 'react-modal';
import Select, { MultiValue } from 'react-select';
import 'react-datepicker/dist/react-datepicker.css';
import { IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchApi from '../../api/platform/searchApi';
import closeIcon from '../../assets/images/close.png';
import ConfirmationModal from '../../components/Common/ConfirmationModal';
import { useSystemError } from '../../components/Error/SystemErrorContext';
import SaveModal from '../../components/Search/SaveModal';
import styles from '../../css/search/search.module.css';
import { CompanyProfileModel } from '../../models/data-sections/company/profileModel';
import { SearchesModel } from '../../models/platform/searchesModel';
import { SearchCriteriaModel } from '../../models/platform/searchCriteriaModel';
import { IndustrySpecialtyModel } from '../../models/data-sections/industry/specialtyModel';
import { IndustrySubSpecialtyModel } from '../../models/data-sections/industry/subSpecialtyModel';
import { NumericRangeModel } from '../../models/system/numericRangeModel';
import { ResponseErrorModel } from '../../models/system/responseErrorModel';
import { ResponseSuccessModel } from '../../models/system/responseSuccessModel';
import { handleError, mapResponseErrorToSystemError, mapUnknownErrorToSystemError } from '../../utils/errorUtils';
import { saveSearch } from '../../utils/searchUtils';


interface SearchModalProps {
  isOpen: boolean;
  onRequestClose: (search: SearchesModel | null, wasSaved: boolean, wasExecuted: boolean, isNewSearch: boolean) => void;
  editedSearch: SearchesModel | null;
}

const SearchModal: React.FC<SearchModalProps> = ({ isOpen, onRequestClose, editedSearch }) => {
  const [countries, setCountries] = useState<MultiValue<{ value: string; label: string }>>([]);
  const [sectors, setSectors] = useState<MultiValue<{ value: string; label: string }>>([]);
  const [companies, setCompanies] = useState<CompanyProfileModel[]>([]);
  const [specialties, setSpecialties] = useState<IndustrySpecialtyModel[]>([]);
  const [subSpecialties, setSubSpecialties] = useState<IndustrySubSpecialtyModel[]>([]);
  const [filteredSubSpecialties, setFilteredSubSpecialties] = useState<IndustrySubSpecialtyModel[]>([]);
  const [marketCapError, setMarketCapError] = useState<string | null>(null);
  const [openTextError, setOpenTextError] = useState<string | null>(null);
  const [revenueError, setRevenueError] = useState<string | null>(null);
  const [dateError, setDateError] = useState<string | null>(null);
  const [isCancelConfirmationModalOpen, setIsCancelConfirmationModalOpen] = useState(false);
  const [isExecuteConfirmationModalOpen, setIsExecuteConfirmationModalOpen] = useState(false);
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [searchName, setSearchName] = useState('');
  const [isAdvancedSearch, setIsAdvancedSearch] = useState(false);
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [stockSymbol, setStockSymbol] = useState('');
  const [revenue, setRevenue] = useState<NumericRangeModel | undefined>(undefined);
  const [marketCap, setMarketCap] = useState<NumericRangeModel | undefined>(undefined);
  const [openText, setOpenText] = useState('');
  const [selectedCompanies, setSelectedCompanies] = useState<MultiValue<CompanyProfileModel>>([]);
  const [selectedSpecialties, setSelectedSpecialties] = useState<MultiValue<IndustrySpecialtyModel>>([]);
  const [selectedSubSpecialties, setSelectedSubSpecialties] = useState<MultiValue<IndustrySubSpecialtyModel>>([]);
  const [selectedCountries, setSelectedCountries] = useState<MultiValue<{ value: string; label: string }>>([]);
  const [selectedSectors, setSelectedSectors] = useState<MultiValue<{ value: string; label: string }>>([]);
  const [dataFetched, setDataFetched] = useState(false);
  const [isEdited, setIsEdited] = useState(false);
  const [loading, setLoading] = useState(true);
  const [shouldExecuteAfterSave, setShouldExecuteAfterSave] = useState(false);
  const [didErrorOccur, setDidErrorOccur] = useState(false);
  const { triggerSystemError } = useSystemError();


  /* useEffect and fetches */
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'; // Lock scroll when modal opens
    } else {
      document.body.style.overflow = ''; // Restore scroll when modal closes
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    const fetchData = async () => {
      if (didErrorOccur) return;

      try {
        setLoading(true);

        const response = await SearchApi.getOptions();
        if (response.success) {
          const data = (response as ResponseSuccessModel).data;

          setCompanies(data.companies);
          setSpecialties(data.specialties);
          setSubSpecialties(data.sub_specialties);

          setCountries(
            data.countries.map((c: string) => ({ value: c, label: c }))
          );

          setSectors(
            data.sectors.map((s: string) => ({ value: s, label: s }))
          );

        } else {
          handleError(mapResponseErrorToSystemError(response as ResponseErrorModel), null, setDidErrorOccur, triggerSystemError);
        }
      } catch (error) {
        handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    try {
      if (isOpen) {
        if (editedSearch) {
          setLoading(true);

          const search_criteria = editedSearch.search_criteria as SearchCriteriaModel;

          setSearchName(editedSearch.search_name || '');

          setIsAdvancedSearch(search_criteria.is_advanced_search || false);
          setStartDate(search_criteria.date_range.start_date ? new Date(search_criteria.date_range.start_date) : undefined);
          setEndDate(search_criteria.date_range.end_date ? new Date(search_criteria.date_range.end_date) : undefined);

          setSelectedCompanies(search_criteria.companies?.map((selectedCompany) => ({
            company_key: selectedCompany.company_key,
            company_name: selectedCompany.company_name,
          })) || []);

          setSelectedSpecialties(search_criteria.specialties?.map((selectedSpecialty) => ({
            specialty_key: selectedSpecialty.specialty_key,
            specialty: selectedSpecialty.specialty,
          })) || []);

          setSelectedSubSpecialties(search_criteria.sub_specialties?.map((selectedSubSpecialty) => ({
            sub_specialty_key: selectedSubSpecialty.sub_specialty_key,
            sub_specialty_name: selectedSubSpecialty.sub_specialty_name,
            specialty_key: selectedSubSpecialty.specialty_key,
          })) || []);

          setSelectedCountries(search_criteria.countries?.map((country) => ({
            value: country,
            label: country,
          })) || []);

          setSelectedSectors(search_criteria.sectors?.map((sector) => ({
            value: sector,
            label: sector,
          })) || []);

          setStockSymbol(search_criteria.stock_symbol || '');
          setMarketCap(search_criteria.market_cap || undefined);
          setRevenue(search_criteria.revenue || undefined);
          setOpenText(search_criteria.open_text || '');
        }
      }
    } catch (error) {
      handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);
    } finally {
      setLoading(false);
    }
  }, [isOpen, editedSearch]);

  useEffect(() => {
    try {
      if (selectedSpecialties.length === 0) {
        setFilteredSubSpecialties([]);
      } else {
        const selectedSpecialtyKeys = selectedSpecialties.map(s => s.specialty_key);
        setFilteredSubSpecialties(subSpecialties.filter(sub => selectedSpecialtyKeys.includes(sub.specialty_key)));
      }
    } catch (error) {
      handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);
    }
  }, [selectedSpecialties, subSpecialties]);

  useEffect(() => {
    try {
      if (!isOpen) {
        resetFields();
      }
    } catch (error) {
      handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);
    }
  }, [isOpen]);


  /* functions */
  const captureSearchCriteria = (): SearchCriteriaModel | null => {
    try {
      if (!startDate || !endDate) return null;

      return {
        date_range: { start_date: startDate, end_date: endDate },
        companies: selectedCompanies.map(c => ({
          company_key: c.company_key,
          company_name: c.company_name,
        })),
        specialties: selectedSpecialties.map(s => ({
          specialty_key: s.specialty_key,
          specialty: s.specialty,
        })),
        sub_specialties: selectedSubSpecialties.map(sub => ({
          sub_specialty_key: sub.sub_specialty_key,
          sub_specialty_name: sub.sub_specialty_name,
          specialty_key: sub.specialty_key,
        })),
        is_advanced_search: isAdvancedSearch,
        stock_symbol: isAdvancedSearch ? stockSymbol : undefined,
        revenue: isAdvancedSearch ? revenue : undefined,
        market_cap: isAdvancedSearch ? marketCap : undefined,
        countries: isAdvancedSearch ? selectedCountries.map(c => c.value) : undefined,
        sectors: isAdvancedSearch ? selectedSectors.map(s => s.value) : undefined,
        open_text: openText,
      };
    } catch (error) {
      handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);
      return null;
    }
  };

  const resetFields = () => {
    try {
      setIsEdited(false);
      setSearchName('');

      setStartDate(undefined);
      setEndDate(undefined);
      setSelectedCompanies([]);
      setSelectedSpecialties([]);
      setSelectedSubSpecialties([]);
      setIsAdvancedSearch(false);
      setFilteredSubSpecialties([]);
      setDataFetched(false);
      setDateError(null);

      resetAdvancedFields();
    } catch (error) {
      handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);
    }
  };

  const resetAdvancedFields = () => {
    try {
      setSelectedCountries([]);
      setSelectedSectors([]);
      setStockSymbol('');
      setRevenue(undefined);
      setMarketCap(undefined);
      setOpenText('');
      setMarketCapError(null);
      setRevenueError(null);
      setOpenTextError(null);
    } catch (error) {
      handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);
    }
  };

  const validateForm = () => {
    try {
      if (!startDate || !endDate) {
        setDateError('Please select both start and end dates.');
        return false;
      }

      if (endDate < startDate) {
        setDateError('The end date must be greater than or equal to the start date.');
        return false;
      }

      setDateError(null);
      return true;
    } catch (error) {
      handleError(mapUnknownErrorToSystemError(error, false), setDateError, setDidErrorOccur, triggerSystemError);
    }
  };


  /* handlers */
  const handleAdvancedSearchToggle = () => {
    try {
      if (isAdvancedSearch) {
        resetAdvancedFields();
      }

      setIsAdvancedSearch(!isAdvancedSearch);
    } catch (error) {
      handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);
    }
  };

  const handleAnnualRevenueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      let input = e.target.value.trim();

      if (input === '') {
        setRevenue(undefined);
        setRevenueError(null);
        setIsEdited(true);
        return;
      }

      const firstChar = input.charAt(0);

      if (firstChar !== '>' && firstChar !== '<') {
        setRevenue(undefined);
        setRevenueError('Please start with a valid operator (< or >)');
        setIsEdited(true);
        return;
      }

      let valueStr = input.slice(1).replace(/[^0-9]/g, '');
      if (valueStr.length > 14) {
        valueStr = valueStr.slice(0, 14);
      }

      if (valueStr === '') {
        setRevenue({ operator: firstChar as '>' | '<', value: null });
        setRevenueError(null);
        setIsEdited(true);
        return;
      }

      const value = parseInt(valueStr, 10);

      setRevenue({ operator: firstChar as '>' | '<', value });
      setRevenueError(null);
      setIsEdited(true);
    } catch (error) {
      handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);
    }
  };

  const handleCompanyChange = (selected: MultiValue<CompanyProfileModel>) => {
    try {
      setSelectedCompanies(selected);
      setIsEdited(true);
    } catch (error) {
      handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);
    }
  };

  const handleCountryChange = (selected: MultiValue<{ value: string; label: string }>) => {
    try {
      setSelectedCountries(selected);
      setIsEdited(true);
    } catch (error) {
      handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);
    }
  };

  const handleEndDateChange = (date: Date | null) => {
    try {
      setEndDate(date ?? undefined);
      setIsEdited(true);
      setDateError('');
    } catch (error) {
      handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);
    }
  };

  const handleMarketCapChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      let input = e.target.value.trim();

      if (input === '') {
        setMarketCap(undefined);
        setMarketCapError(null);
        setIsEdited(true);
        return;
      }

      const firstChar = input.charAt(0);

      if (firstChar !== '>' && firstChar !== '<') {
        setMarketCap(undefined);
        setMarketCapError('Please start with a valid operator (< or >)');
        setIsEdited(true);
        return;
      }

      let valueStr = input.slice(1).replace(/[^0-9]/g, '');
      if (valueStr.length > 14) {
        valueStr = valueStr.slice(0, 14);
      }

      if (valueStr === '') {
        setMarketCap({ operator: firstChar as '>' | '<', value: null });
        setMarketCapError(null);
        setIsEdited(true);
        return;
      }

      const value = parseInt(valueStr, 10);

      setMarketCap({ operator: firstChar as '>' | '<', value });
      setMarketCapError(null);
      setIsEdited(true);
    } catch (error) {
      handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);
    }
  };

  const handleSectorChange = (selected: MultiValue<{ value: string; label: string }>) => {
    try {
      setSelectedSectors(selected);
      setIsEdited(true);
    } catch (error) {
      handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);
    }
  };

  const handleSpecialtyChange = (selected: MultiValue<IndustrySpecialtyModel>) => {
    try {
      setSelectedSpecialties(selected);
      const selectedSpecialtyKeys = selected.map(s => s.specialty_key);
      const filteredSubSpecialties = subSpecialties.filter(sub => selectedSpecialtyKeys.includes(sub.specialty_key));
      setFilteredSubSpecialties(filteredSubSpecialties);

      const filteredSelectedSubSpecialties = selectedSubSpecialties.filter(sub => selectedSpecialtyKeys.includes(sub.specialty_key));
      setSelectedSubSpecialties(filteredSelectedSubSpecialties);

      setIsEdited(true);
    } catch (error) {
      handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);
    }
  };

  const handleStartDateChange = (date: Date | null) => {
    try {
      setStartDate(date ?? undefined);
      setIsEdited(true);
      setDateError('');
    } catch (error) {
      handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);
    }
  };

  const handleSubSpecialtyChange = (selected: MultiValue<IndustrySubSpecialtyModel>) => {
    try {
      setSelectedSubSpecialties(selected);
      setIsEdited(true);
    } catch (error) {
      handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);
    }
  };

  const handleStockSymbolChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      let value = e.target.value.toUpperCase();
      if (value.length <= 5 && /^[A-Z]*$/.test(value)) {
        setStockSymbol(value);
        setIsEdited(true);
      }
    } catch (error) {
      handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);
    }
  };

  const handleOpenTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const input = e.target.value;

      const wordCount = input.trim().split(/\s+/).length;
      if (wordCount > 4) {
        setOpenTextError('Open text must be no more than 4 words');
        return;
      }

      if (input.length > 100) {
        setOpenTextError('Open text must be under 100 characters');
        return;
      }

      setOpenTextError('');
      setOpenText(input);
      setIsEdited(true);
    } catch (error) {
      handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);
    }
  };


  /* button handles */
  const handleCancelConfirm = () => {
    try {
      resetFields();
      setIsCancelConfirmationModalOpen(false);
      onRequestClose(null, true, false, false);
    } catch (error) {
      handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);
    }
  };

  const handleCancelClose = () => {
    try {
      setIsCancelConfirmationModalOpen(false);
    } catch (error) {
      handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);
    }
  };

  const handleCancelRequest = () => {
    try {
      if (isEdited) {
        setIsCancelConfirmationModalOpen(true);
      } else {
        onRequestClose(null, false, false, false);
      }
    } catch (error) {
      handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);
    }
  };

  const handleExecuteCancel = () => {
    try {
      setIsExecuteConfirmationModalOpen(false);
    } catch (error) {
      handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);
    }
  };

  const handleExecuteButtonClick = () => {
    try {
      if (!validateForm()) return;
      setIsExecuteConfirmationModalOpen(true);
    } catch (error) {
      handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);
    }
  };

  const handleExecuteConfirm = () => {
    try {

      const searchCriteria = captureSearchCriteria();
      if (!searchCriteria) {
        handleError(mapUnknownErrorToSystemError(new Error('Invalid search criteria'), false), null, setDidErrorOccur, triggerSystemError);
        return;
      }

      const adHocSearch = {
        search_criteria: searchCriteria,
        _id: editedSearch?._id ?? null,
        search_name: editedSearch?.search_name || '',
      } as SearchesModel;

      onRequestClose(adHocSearch, false, true, true);
      resetFields();
      setIsExecuteConfirmationModalOpen(false);
    } catch (error) {
      handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);
    }
  };

  /* validates form, then opens modal SaveModal.tsx */
  const handleSaveExecuteButtonClick = () => {
    try {
      if (!validateForm()) return;

      setShouldExecuteAfterSave(true);
      setIsSaveModalOpen(true);
    } catch (error) {
      handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);
    }
  };

  const handleSaveSearch = async (searchName: string) => {
    const searchCriteria = captureSearchCriteria();

    await saveSearch({
      searchName,
      searchCriteria,
      editedSearch,
      shouldExecuteAfterSave,
      setIsSaveModalOpen,
      onRequestClose,
      triggerSystemError,
    });
  };


  return (
    <>
      <ReactModal
        isOpen={isOpen}
        onRequestClose={() => onRequestClose(null, true, false, true)}
        contentLabel="New Search"
        className={`${styles.modal} ${isAdvancedSearch ? styles.advancedModal : ''}`}
        overlayClassName={styles.overlay}
        shouldCloseOnOverlayClick={false}
      >
        {loading ? (
          <div className={styles.spinnerContainer}>
            <div className={styles.spinner}></div> {/* Spinner */}
          </div>
        ) : (
          <>
            <div className={styles.modalHeader}>
              <button onClick={handleAdvancedSearchToggle} className={styles.advancedSearchButton}>
                {isAdvancedSearch ? 'Back to General Search' : 'Go to Advanced Search'}
              </button>
              <IconButton
                onClick={() => onRequestClose(null, true, false, false)} // Use the same logic as your Dialog
                style={{ position: 'absolute', right: 8, top: 8 }}
              >
                <CloseIcon />
              </IconButton>
            </div>
            <div className={styles.modalContent}>
              {!isAdvancedSearch ? (
                <div>
                  <div className={styles.field}>
                    <label className={styles.label}>Date Range</label>
                    <div className={styles.dateRange}>
                      <DatePicker
                        selected={startDate}
                        onChange={handleStartDateChange}
                        selectsStart
                        startDate={startDate}
                        endDate={endDate}
                        placeholderText="Start Date"
                        className={styles.datePicker}
                      />
                      <DatePicker
                        selected={endDate}
                        onChange={handleEndDateChange}
                        selectsEnd
                        startDate={startDate}
                        endDate={endDate}
                        placeholderText="End Date"
                        className={styles.datePicker}
                      />
                    </div>
                    {dateError && <div className={styles.errorMessage}>{dateError}</div>}
                  </div>
                  <div className={styles.field}>
                    <label className={styles.label}>Company</label>
                    <Select
                      isMulti
                      value={selectedCompanies}
                      onChange={handleCompanyChange}
                      options={companies}
                      getOptionLabel={(option) => option.company_name}
                      getOptionValue={(option) => option.company_key}
                      className={styles.dropdown}
                      classNamePrefix="react-select"
                      menuPortalTarget={document.body}
                      styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    />
                  </div>
                  <div className={styles.field}>
                    <label className={styles.label}>Specialty</label>
                    <Select
                      isMulti
                      value={selectedSpecialties}
                      onChange={handleSpecialtyChange}
                      options={specialties}
                      getOptionLabel={(option) => option.specialty}
                      getOptionValue={(option) => option.specialty_key}
                      className={styles.dropdown}
                      menuPortalTarget={document.body}
                      styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    />
                  </div>
                  <div className={styles.field}>
                    <label className={styles.label}>Sub-Specialty</label>
                    <Select
                      isMulti
                      value={selectedSubSpecialties}
                      onChange={handleSubSpecialtyChange}
                      options={filteredSubSpecialties}
                      getOptionLabel={(option) => option.sub_specialty_name}
                      getOptionValue={(option) => option.sub_specialty_key}
                      className={styles.dropdown}
                      menuPortalTarget={document.body}
                      styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    />
                  </div>
                </div>
              ) : (
                <div className={styles.advancedSearchContainer}>
                  <div className={styles.dateRow}>
                    <div className={styles.field}>
                      <label className={styles.label}>Date Range</label>
                      <div className={styles.dateRange}>
                        <DatePicker
                          selected={startDate}
                          onChange={handleStartDateChange}
                          selectsStart
                          startDate={startDate}
                          endDate={endDate}
                          placeholderText="Start Date"
                          className={styles.datePicker}
                        />
                        <DatePicker
                          selected={endDate}
                          onChange={handleEndDateChange}
                          selectsEnd
                          startDate={startDate}
                          endDate={endDate}
                          placeholderText="End Date"
                          className={styles.datePicker}
                        />
                      </div>
                      {dateError && <div className={styles.errorMessage}>{dateError}</div>}
                    </div>
                  </div>
                  <div className={styles.gridContainer}>
                    <div className={styles.gridLeft}>
                      <div className={styles.field}>
                        <label className={styles.label}>Company</label>
                        <Select
                          isMulti
                          value={selectedCompanies}
                          onChange={handleCompanyChange}
                          options={companies}
                          getOptionLabel={(option) => option.company_name}
                          getOptionValue={(option) => option.company_key}
                          className={styles.dropdown}
                          classNamePrefix="react-select"
                          menuPortalTarget={document.body}
                          styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                        />
                      </div>
                      <div className={styles.field}>
                        <label className={styles.label}>Specialty</label>
                        <Select
                          isMulti
                          value={selectedSpecialties}
                          onChange={handleSpecialtyChange}
                          options={specialties}
                          getOptionLabel={(option) => option.specialty}
                          getOptionValue={(option) => option.specialty_key}
                          className={styles.dropdown}
                          menuPortalTarget={document.body}
                          styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                        />
                      </div>
                      <div className={styles.field}>
                        <label className={styles.label}>Sub-Specialty</label>
                        <Select
                          isMulti
                          value={selectedSubSpecialties}
                          onChange={handleSubSpecialtyChange}
                          options={filteredSubSpecialties}
                          getOptionLabel={(option) => option.sub_specialty_name}
                          getOptionValue={(option) => option.sub_specialty_key}
                          className={styles.dropdown}
                          menuPortalTarget={document.body}
                          styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                        />
                      </div>
                    </div>
                    <div className={styles.gridMiddle}>
                      <div className={styles.gridItem}>
                        <label className={styles.label}>Country</label>
                        <Select
                          isMulti
                          value={selectedCountries}
                          onChange={handleCountryChange}
                          options={countries}
                          className={styles.dropdown}
                          classNamePrefix="react-select"
                          menuPortalTarget={document.body}
                          styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                        />
                      </div>
                      <div className={styles.gridItem}>
                        <label className={styles.label}>Sector</label>
                        <Select
                          isMulti
                          value={selectedSectors}
                          onChange={handleSectorChange}
                          options={sectors}
                          className={styles.dropdown}
                          classNamePrefix="react-select"
                          menuPortalTarget={document.body}
                          styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                        />
                      </div>
                    </div>
                    <div className={styles.gridRight}>
                      <div className={styles.gridItem}>
                        <label className={styles.label}>Symbol</label>
                        <input
                          type="text"
                          value={stockSymbol}
                          onChange={handleStockSymbolChange}
                          className={styles.input}
                          maxLength={5}
                        />
                      </div>
                      <div className={styles.gridItem}>
                        <label className={styles.label}>Market Capitalization</label>
                        <input
                          type="text"
                          value={
                            marketCap && marketCap.value !== null
                              ? `${marketCap.operator} ${marketCap.value.toLocaleString()}`
                              : marketCap ? marketCap.operator : ''
                          }
                          onChange={handleMarketCapChange}
                          className={styles.input}
                        />
                        {marketCapError && (
                          <div className={styles.errorMessage}>
                            {marketCapError}
                          </div>
                        )}
                      </div>
                      <div className={styles.gridItem}>
                        <label className={styles.label}>Annual Revenue</label>
                        <input
                          type="text"
                          value={
                            revenue && revenue.value !== null
                              ? `${revenue.operator} ${revenue.value?.toLocaleString()}`
                              : revenue ? revenue.operator : ''
                          }
                          onChange={handleAnnualRevenueChange}
                          className={styles.input}
                        />
                        {revenueError && (
                          <div className={styles.errorMessage}>
                            {revenueError}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className={styles.field}>
                    <label className={styles.label}>Open Text</label>
                    <input
                      type="text"
                      value={openText}
                      onChange={handleOpenTextChange}
                      className={`${styles.input}`}
                    />
                    {openTextError && <div className={styles.errorMessage}>{openTextError}</div>}
                  </div>
                </div>
              )}
            </div>
            <div className={styles.buttonContainer}>
              <button className={`${styles.newSearchButton} ${styles.gradientBlueButton}`} onClick={handleSaveExecuteButtonClick}>
                Save + Execute
              </button>
              <button className={`${styles.newSearchButton} ${styles.gradientGreenButton}`} onClick={handleExecuteButtonClick}>
                Execute Only
              </button>
            </div>
          </>
        )}
      </ReactModal>

      <SaveModal
        isOpen={isSaveModalOpen}
        onRequestClose={() => setIsSaveModalOpen(false)}
        onSave={(searchName) => handleSaveSearch(searchName)}
        initialSearchName={editedSearch?._id ? searchName ?? '' : ''}
        isEditMode={!!editedSearch?._id}
      />

      {/* Execute Confirmation Modal */}
      <ConfirmationModal
        isOpen={isExecuteConfirmationModalOpen}
        onClose={handleExecuteCancel}
        onConfirm={handleExecuteConfirm}
        title="Execute Search"
        message="Are you sure you want to execute this search? This action might take a while."
        confirmationType="execute"
      />

      <ConfirmationModal
        isOpen={isCancelConfirmationModalOpen}
        onClose={handleCancelClose}
        onConfirm={handleCancelConfirm}
        title="Cancel"
        message="Are you sure you want to close the search without saving?"
      />
    </>
  );
};

export default SearchModal;
